import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, CheckCircle2, Siren, Mail } from "lucide-react";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  auto: "Auto Detailing",
  habitat: "Habitat & Extérieurs",
  preventif: "Entretien préventif (neuf)",
  pro: "Professionnels / Entreprises",
  entretien: "Contrat d'entretien",
  urgence: "Urgence / Sinistre",
};

export const LeadDialog = ({ open, onOpenChange, service = "auto" }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const [serviceType, setServiceType] = useState(service);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setServiceType(service);
      setDone(false);
    }
  }, [open, service, setServiceType, setDone]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Merci d'indiquer votre nom et votre téléphone.");
      return;
    }
    if (!form.city.trim()) {
      toast.error("Merci d'indiquer la commune d'intervention (pour le déplacement).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        city: form.city,
        service_type: serviceType,
        message: form.message || null,
        is_urgent: serviceType === "urgence",
      });
      setDone(true);
      toast.success("Demande envoyée ! Nous vous rappelons rapidement.");
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-form"
        className="sm:max-w-lg rounded-sm"
      >
        {done ? (
          <div className="py-8 text-center" data-testid="quote-success">
            <CheckCircle2 className="w-16 h-16 text-ink mx-auto" />
            <h3 className="font-heading font-extrabold text-2xl text-ink mt-4">
              Demande bien reçue
            </h3>
            <p className="text-muted-foreground mt-2">
              Merci ! Christophe Martin vous recontacte dans les plus brefs délais.
            </p>
            <button
              data-testid="quote-close-btn"
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-ink text-white font-bold px-6 py-3 rounded-sm hover:bg-ink-light transition-colors"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-extrabold text-2xl text-ink flex items-center gap-2">
                {serviceType === "urgence" && <Siren className="w-6 h-6 text-ink" />}
                Demander mon devis gratuit
              </DialogTitle>
              <DialogDescription>
                Décrivez votre besoin, nous revenons vers vous rapidement.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="lead-service">Prestation souhaitée</Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger
                    id="lead-service"
                    data-testid="lead-service-select"
                    className="mt-1.5 rounded-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`service-option-${k}`}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lead-name">Nom *</Label>
                  <Input
                    id="lead-name"
                    data-testid="lead-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Votre nom"
                    className="mt-1.5 rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="lead-phone">Téléphone *</Label>
                  <Input
                    id="lead-phone"
                    data-testid="lead-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="06 xx xx xx xx"
                    className="mt-1.5 rounded-sm"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lead-city">Commune d'intervention *</Label>
                <Input
                  id="lead-city"
                  data-testid="lead-city-input"
                  value={form.city}
                  onChange={set("city")}
                  placeholder="Ex : Belfort, Montbéliard, Héricourt…"
                  className="mt-1.5 rounded-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Permet d'estimer le déplacement au plus juste.
                </p>
              </div>

              <div>
                <Label htmlFor="lead-email">Email (optionnel)</Label>
                <Input
                  id="lead-email"
                  data-testid="lead-email-input"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="vous@email.com"
                  className="mt-1.5 rounded-sm"
                />
              </div>

              <div>
                <Label htmlFor="lead-message">
                  Description de l'état / de l'urgence
                </Label>
                <Textarea
                  id="lead-message"
                  data-testid="lead-message-input"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Décrivez votre véhicule, surface ou situation…"
                  rows={3}
                  className="mt-1.5 rounded-sm"
                />
              </div>

              <button
                type="submit"
                data-testid="lead-submit-btn"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-ink text-white font-bold px-6 py-4 rounded-sm hover:bg-ink-light transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                {loading ? "Envoi…" : "Demander mon devis gratuit"}
              </button>

              <p className="text-center text-xs text-muted-foreground pt-1">
                Vous préférez l'email ? Écrivez-nous à{" "}
                <a
                  href={`mailto:${CONTACT.quoteEmail}`}
                  data-testid="lead-quote-email"
                  className="inline-flex items-center gap-1 font-semibold text-ink hover:underline"
                >
                  <Mail className="w-3.5 h-3.5" /> {CONTACT.quoteEmail}
                </a>
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
